import { CornerUpLeftIcon } from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useGetApiSuiteResults } from "../../../useQuery";

const SuiteResults = ({ suite, onBack, onViewSingleResult }) => {
  const { data, isLoading, isError, error } = useGetApiSuiteResults(suite.id);

  const latestResult = data && data.length > 0 ? data[0] : null;

  const pieData = latestResult
    ? [
        { name: "Pass", value: latestResult.passCount, color: "green" },
        { name: "Fail", value: latestResult.failCount, color: "red" },
      ]
    : [];

  const formatDate = (dateString) => {
    return (
      new Date(dateString).toLocaleDateString() + " " + new Date(dateString).toLocaleTimeString()
    );
  };

  if (isLoading) {
    return (
      <div className="font-apple  bg-white h-full max-h-[100vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="font-apple  bg-white h-full max-h-[100vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-center h-full">
          <div className="text-red-500">Error loading data</div>
        </div>
      </div>
    );
  }

  return (
    <div className="font-apple  bg-white h-full max-h-[100vh] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-3 py-3 text-sm text-gray-600 hover:text-gray-900 hover:bg-blue-300 rounded-full transition-colors cursor-pointer"
          >
            <CornerUpLeftIcon size={24} />
          </button>
          <div className="h-6 w-px bg-gray-300"></div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Results For: {suite.testSuiteName} suite
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 hover:scrollbar-thumb-gray-500">
        {/* Latest Results Overview */}
        {latestResult && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Latest Test Results</h2>

            {/* Stats Chips */}
            <div className="flex gap-4 mb-6">
              <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
                ✓ {latestResult.passCount} Passed
              </div>
              <div className="bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-medium">
                ✗ {latestResult.failCount} Failed
              </div>
              <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium">
                ⏱ {latestResult.duration}s Duration
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Pie Chart */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-md font-medium text-gray-900 mb-2">Pass/Fail Distribution</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Bar Chart */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-md font-medium text-gray-900 mb-2">Test Results</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={pieData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* All Results Table */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Execution History</h2>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead className="bg-blue-100 ">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider">
                    Suite Name
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider">
                    Executed Date
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider">
                    Pass Count
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider">
                    Fail Count
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data &&
                  data.map((result) => (
                    <tr key={result.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {result.testSuiteName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(result.executedDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {result.duration}s
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {result.passCount}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {result.failCount}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => onViewSingleResult(result)}
                          className="cursor-pointer text-black bg-blue-100 px-3 py-1 transition-colors"
                        >
                          View Suite Results
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Empty state */}
        {data && data.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500">No test results found</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuiteResults;
